import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "name", "code", "feedback", "submit" ]
  static values = {
    url: String,
    isPersisted: Boolean,
    availableMessage: String,
    invalidMessage: String
  }

  initialize() {
    this.userCustomized = false
    this.timeout = null
  }

  connect() {
    if (this.isPersistedValue) {
      // If editing, we do not perform live generation or validation
      return
    }
  }

  nameChanged() {
    if (this.isPersistedValue || this.userCustomized) return

    clearTimeout(this.timeout)
    const nameValue = this.nameTarget.value.trim()

    if (nameValue === "") {
      this.codeTarget.value = ""
      this.clearFeedback()
      this.enableSubmit()
      return
    }

    this.timeout = setTimeout(() => {
      this.fetchSuggestedCode(nameValue)
    }, 300) // Debounce backend call
  }

  codeChanged() {
    if (this.isPersistedValue) return

    const codeValue = this.codeTarget.value.toUpperCase().replace(/[^A-Z]/g, '')
    this.codeTarget.value = codeValue

    if (codeValue === "") {
      // If user cleared the code, reset to auto-generation
      this.userCustomized = false
      this.clearFeedback()
      this.nameChanged()
      return
    }

    this.userCustomized = true
    clearTimeout(this.timeout)

    this.timeout = setTimeout(() => {
      this.validateCustomCode(codeValue)
    }, 300) // Debounce backend call
  }

  async fetchSuggestedCode(name) {
    try {
      const separator = this.urlValue.includes("?") ? "&" : "?"
      const response = await fetch(`${this.urlValue}${separator}name=${encodeURIComponent(name)}`)
      const data = await response.json()
      if (data.valid && data.suggested_code) {
        this.codeTarget.value = data.suggested_code
        this.clearFeedback()
        this.enableSubmit()
      }
    } catch (error) {
      console.error("Error fetching suggested code:", error)
    }
  }

  async validateCustomCode(code) {
    try {
      const separator = this.urlValue.includes("?") ? "&" : "?"
      const response = await fetch(`${this.urlValue}${separator}code=${encodeURIComponent(code)}`)
      const data = await response.json()
      if (data.valid) {
        this.showSuccess(this.availableMessageValue)
        this.enableSubmit()
      } else {
        this.showError(data.error || this.invalidMessageValue)
        this.disableSubmit()
      }
    } catch (error) {
      console.error("Error validating code:", error)
    }
  }

  showError(message) {
    this.feedbackTarget.textContent = message
    this.feedbackTarget.className = "text-xs mt-1 text-red-600 dark:text-red-400 font-medium"
    this.codeTarget.classList.add("border-red-500", "focus:ring-red-500", "focus:border-red-500")
    this.codeTarget.classList.remove("border-green-500", "focus:ring-green-500", "focus:border-green-500")
  }

  showSuccess(message) {
    this.feedbackTarget.textContent = message
    this.feedbackTarget.className = "text-xs mt-1 text-green-600 dark:text-green-400 font-medium"
    this.codeTarget.classList.add("border-green-500", "focus:ring-green-500", "focus:border-green-500")
    this.codeTarget.classList.remove("border-red-500", "focus:ring-red-500", "focus:border-red-500")
  }

  clearFeedback() {
    this.feedbackTarget.textContent = ""
    this.feedbackTarget.className = "text-xs mt-1"
    this.codeTarget.classList.remove(
      "border-red-500", "focus:ring-red-500", "focus:border-red-500",
      "border-green-500", "focus:ring-green-500", "focus:border-green-500"
    )
  }

  enableSubmit() {
    this.submitTarget.disabled = false
    this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
  }

  disableSubmit() {
    this.submitTarget.disabled = true
    this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
  }
}
